import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { ToggleSwitch } from '@/components/ToggleSwitch';

/** `/settings/features` (利用機能タブ、v0.2.5 S-11)。platform_admin のみ編集可、company_admin は
 *  feature_catalog.editable_by='company_admin' の機能のみ編集可 (それ以外は閲覧のみ)。
 *
 * v0.4 章③ (D-90/D-94/D-116, §8-93 の構造的解消): 編集可能ロールはハードコードの Set ではなく
 * API (`GET /tenant-features` の `editable_by`) から取得する。旧 EDITABLE_BY_COMPANY_ADMIN は
 * apps/api/src/routes/tenant-features.ts との二重定義になっており、片方だけ更新すると
 * 「実装したのに永久にONにできない機能」が生まれる事故があった (§8-93)。DB 化によりこの面が消える。 */
type Feature = { feature_code: string; label: string; description: string; is_enabled: boolean; editable_by: 'platform_admin' | 'company_admin' };

/** §8-176: `s.user?.roles ?? []` は `s.user` 未確定の間セレクタが呼び出しのたびに新しい配列を
 *  返し、useSyncExternalStore の snapshot 不一致で無限レンダーループを起こす。フォールバックは
 *  モジュールスコープの安定した参照にする (docs/known-pitfalls.md §8-176参照)。 */
const EMPTY_ROLES: string[] = [];

export function FeaturesSettingsPage() {
  const qc = useQueryClient();
  const authUser = useAuthStore((s) => s.user);
  const roles = authUser?.roles ?? EMPTY_ROLES;
  const isPlatformAdmin = roles.includes('platform_admin');
  const isCompanyAdmin = isPlatformAdmin || roles.includes('company_admin');
  const { data: features = [], isLoading } = useQuery<Feature[]>({ queryKey: ['tenant-features'], queryFn: () => apiFetch<Feature[]>('/tenant-features') });

  const toggleMut = useMutation({
    mutationFn: (v: { code: string; is_enabled: boolean }) =>
      apiFetch(`/tenant-features/${v.code}`, { method: 'PATCH', body: JSON.stringify({ is_enabled: v.is_enabled }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tenant-features'] }); toast.success('更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  return (
    <div className="space-y-4 max-w-xl">
      <div>
        <h2 className="text-h2">利用機能</h2>
        <p className="text-sm text-neutral-sub mt-1">
          テナントで利用する機能を切り替えます。
          {!isPlatformAdmin && ' (多くの項目の変更にはプラットフォーム管理者権限が必要です、閲覧のみ可能)'}
        </p>
      </div>

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="border border-neutral-line rounded-lg bg-white divide-y divide-neutral-line">
          {features.map((f) => {
            const canEdit = isPlatformAdmin || (f.editable_by === 'company_admin' && isCompanyAdmin);
            return (
            <div key={f.feature_code} className="flex items-center justify-between gap-4 p-4">
              <div className="min-w-0">
                <div className="text-sm font-medium text-neutral-text">{f.label}</div>
                {f.description && <div className="text-xs text-neutral-sub mt-0.5">{f.description}</div>}
              </div>
              <ToggleSwitch
                checked={f.is_enabled}
                disabled={!canEdit || toggleMut.isPending}
                onChange={(v) => toggleMut.mutate({ code: f.feature_code, is_enabled: v })}
                ariaLabel={`${f.label}を${f.is_enabled ? '無効化' : '有効化'}`}
              />
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
